import { identifyUser, resetUser, trackAnalyticsEvent } from '@/integrations/analytics';
import { AppError, ErrorType } from '@/lib/api/types/errors';
import { queryClient } from '@/lib/query-client';
import { getCognitoAppName } from '@/shared/lib/cognitoAppName';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../api/authService';
import type { ChallengeResponse, LoginCredentials, User } from '../types/auth';

/**
 * Map a login failure to a safe, user-friendly message.
 *
 * The backend message must never be shown verbatim for server/network/unknown
 * failures — when the DB or service is unavailable the raw response can contain
 * SQL or stack details that should not reach the login screen. Only 401s carry
 * a safe, intentional "invalid credentials" message.
 */
function getLoginErrorMessage(error: unknown): string {
  if (error instanceof AppError) {
    switch (error.type) {
      case ErrorType.AUTHENTICATION:
        return 'Invalid email or password. Please try again.';
      case ErrorType.NETWORK:
        return 'Unable to connect. Please check your internet connection and try again.';
      case ErrorType.TIMEOUT:
        return 'The request timed out. Please try again.';
      case ErrorType.SERVER:
        return 'Service temporarily unavailable. Please try again in a few minutes.';
      default:
        return 'Unable to sign in right now. Please try again later.';
    }
  }
  return 'Unable to sign in right now. Please try again later.';
}

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pendingChallenge: { session: string; username: string } | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  /** Begin SSO: fetch the Cognito authorize URL and redirect the browser. */
  ssoLogin: (identityProvider?: string) => Promise<void>;
  /** Finish SSO: exchange the OAuth code returned to the callback for a session. */
  completeSsoLogin: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
  clearChallenge: () => void;
  checkAuth: () => Promise<void>;
  refreshUser: () => Promise<void>;
  submitNewPassword: (newPassword: string) => Promise<void>;
  changePassword: (args: { previousPassword: string; proposedPassword: string }) => Promise<void>;
  requestPasswordReset: (username: string) => Promise<void>;
  confirmPasswordReset: (args: {
    username: string;
    verificationCode: string;
    newPassword: string;
  }) => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      pendingChallenge: null,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null, pendingChallenge: null });
        try {
          const response = await authService.login(credentials);
          const data = response.data;

          if ('ChallengeName' in data && data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
            set({
              isLoading: false,
              pendingChallenge: {
                session: (data as ChallengeResponse).Session,
                username: credentials.username,
              },
            });
            return;
          }

          const user = data as User;
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          try {
            const analyticsUserId = user.email || user.username || user.id;
            if (analyticsUserId) {
              identifyUser(analyticsUserId, {
                username: user.username,
                email: user.email,
                fullName: user.fullName,
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.roles?.[0],
                roles: user.roles,
                client: user.client,
                subcontractor: user.subcontractor,
              });
            }
            trackAnalyticsEvent('login_success', { method: 'password' });
          } catch (analyticsError) {
            console.error('Analytics identification failed:', analyticsError);
          }
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: getLoginErrorMessage(error),
          });
          throw error;
        }
      },

      ssoLogin: async (identityProvider?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.ssoInitiate({ identityProvider });
          const url = response?.data?.url;
          if (!url) {
            throw new Error('No SSO URL returned');
          }
          // Full-page redirect to Cognito Hosted UI / federated IdP. The browser
          // returns to the /auth/callback route with an authorization code.
          window.location.href = url;
        } catch (error) {
          set({
            isLoading: false,
            error: getLoginErrorMessage(error),
          });
          throw error;
        }
      },

      completeSsoLogin: async (code: string) => {
        set({ isLoading: true, error: null, pendingChallenge: null });
        try {
          const response = await authService.ssoCallback({ code });
          const user = response.data as User;
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          try {
            const analyticsUserId = user.email || user.username || user.id;
            if (analyticsUserId) {
              identifyUser(analyticsUserId, {
                username: user.username,
                email: user.email,
                fullName: user.fullName,
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.roles?.[0],
                roles: user.roles,
                client: user.client,
                subcontractor: user.subcontractor,
              });
            }
            trackAnalyticsEvent('login_success', { method: 'sso' });
          } catch (analyticsError) {
            console.error('Analytics identification failed:', analyticsError);
          }
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: getLoginErrorMessage(error),
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authService.logout();

          // Reset analytics user
          try {
            resetUser();
            trackAnalyticsEvent('logout');
          } catch (analyticsError) {
            console.error('Analytics reset failed:', analyticsError);
          }
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Drop every cached query. All server state (permissions, jobs, users, dashboard…)
          // is scoped to the logged-in user, and the Header logout is a SPA navigation — not a
          // reload — so without this the next user to log in on this machine is served the
          // previous user's cache (e.g. their RBAC rules, leaving the sidebar showing the wrong
          // permissions until a manual refresh). A hard reload empties this cache; clearing it
          // here reproduces that clean-slate behaviour for every logout path (WRK-5474).
          queryClient.clear();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      clearError: () => {
        set({ error: null });
      },

      clearChallenge: () => {
        set({ pendingChallenge: null });
      },

      submitNewPassword: async (newPassword: string) => {
        const { pendingChallenge } = get();
        if (!pendingChallenge) throw new Error('No pending challenge');

        set({ isLoading: true, error: null });
        try {
          await authService.handleNewPasswordChallenge({
            username: pendingChallenge.username,
            password: newPassword,
            session: pendingChallenge.session,
          });
          set({ pendingChallenge: null, isLoading: false });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to set new password',
          });
          throw error;
        }
      },

      changePassword: async ({ previousPassword, proposedPassword }) => {
        set({ isLoading: true, error: null });
        try {
          // The backend revokes all sessions (Cognito GlobalSignOut) and clears
          // the auth cookies on success, so the session is already dead server-side.
          // We only need to mirror that locally — no separate authService.logout call.
          await authService.changePassword({ previousPassword, proposedPassword });

          try {
            resetUser();
            trackAnalyticsEvent('password_changed');
          } catch (analyticsError) {
            console.error('Analytics reset failed:', analyticsError);
          }

          // Same clean slate as logout: the session is dead server-side and the user must
          // re-authenticate, so drop the previous user's cached server state (WRK-5474).
          queryClient.clear();
          set({ user: null, isAuthenticated: false, isLoading: false, error: null });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      requestPasswordReset: async (username: string) => {
        set({ isLoading: true, error: null });
        try {
          // appName drives Cognito's CustomMessage Lambda template selection —
          // the backend is shared with legacy (Ignite Core) so we identify
          // ourselves explicitly or the wrong template ships.
          await authService.forgotPassword({ username, appName: getCognitoAppName() });
          set({ isLoading: false });
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error
                ? error.message
                : 'Failed to send reset code. Please try again.',
          });
          throw error;
        }
      },

      confirmPasswordReset: async ({ username, verificationCode, newPassword }) => {
        set({ isLoading: true, error: null });
        try {
          await authService.confirmForgotPassword({ username, verificationCode, newPassword });
          set({ isLoading: false });
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error
                ? error.message
                : 'Failed to reset password. Please check the code and try again.',
          });
          throw error;
        }
      },

      checkAuth: async () => {
        const { user } = get();
        if (!user) {
          set({ isAuthenticated: false });
          return;
        }

        set({ isLoading: true });
        try {
          // Verify the current session is still valid
          await authService.isAuthenticated();
          set({ isAuthenticated: true, isLoading: false });
        } catch (_error) {
          // Session is invalid, clear user
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      refreshUser: async () => {
        set({ isLoading: true });
        try {
          const response = await authService.getCurrentUser();
          const user = response.data;
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Update analytics identity after refresh
          try {
            const analyticsUserId = user.email || user.username || user.id;
            if (analyticsUserId) {
              identifyUser(analyticsUserId, {
                username: user.username,
                email: user.email,
                fullName: user.fullName,
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.roles?.[0],
                roles: user.roles,
                client: user.client,
                subcontractor: user.subcontractor,
              });
            }
          } catch (analyticsError) {
            console.error('Analytics identification failed:', analyticsError);
          }
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to refresh user',
          });
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
